import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  accionesPendientes,
  descartarAccion,
  encolarAccion,
  esFaltaDeSenal,
} from '@/lib/campo/colaDeAcciones'
import { conRespaldo } from '@/lib/campo/loUltimoQueSeSupo'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { MiRecogida } from '@/lib/supabase/tipos-bd'

/**
 * PEDIR QUE RECOJAN UNA HERRAMIENTA EN UN PUNTO (0239).
 *
 * Johan: «otra opción, pedir recogida en punto; lo gestiona el operario, y
 * para recogerla —y que se le pueda cerrar la orden al técnico— debe escanear
 * el QR a la hora de devolver». El técnico la deja en un sitio y lo dice; el
 * gerente decide quién pasa; ese operario la lleva a bodega y la escanea. Hasta
 * ese escaneo sigue a nombre del técnico.
 *
 * Todo esto se puede hacer sin señal («que todo pueda gestionarse offline»):
 * la petición y su cancelación se guardan en el teléfono y salen solas.
 */

type Pedido = {
  prestamoId: string
  direccion: string
  latitud: number | null
  longitud: number | null
  nota: string
}

function useRefrescar() {
  const cliente = useQueryClient()
  return async () => {
    await cliente.invalidateQueries({ queryKey: ['mis-herramientas'] })
    await cliente.invalidateQueries({ queryKey: ['mis-recogidas'] })
  }
}

export function usePedirRecogida() {
  const refrescar = useRefrescar()

  return useMutation({
    mutationFn: async (p: Pedido): Promise<{ enCola: boolean }> => {
      const argumentos = {
        p_prestamo_id: p.prestamoId,
        p_direccion: p.direccion.trim(),
        p_latitud: p.latitud,
        p_longitud: p.longitud,
        p_nota: p.nota.trim() || null,
      }
      const guardar = async () => {
        await encolarAccion('recogida', p.prestamoId, argumentos)
        return { enCola: true }
      }
      if (esFaltaDeSenal(null)) return guardar()

      const { error } = await supabase.rpc('fn_pedir_recogida_herramienta', argumentos)
      if (error && esFaltaDeSenal(error)) return guardar()
      if (error) throw new Error(mensajeDeError(error))
      return { enCola: false }
    },
    onSuccess: refrescar,
  })
}

/**
 * Cancelar: si todavía no salió del teléfono, basta con borrarla de la cola;
 * si ya está en la base, se cancela allá (o se guarda la cancelación).
 */
export function useCancelarRecogida() {
  const refrescar = useRefrescar()

  return useMutation({
    mutationFn: async ({
      prestamoId,
      recogidaId,
    }: {
      prestamoId: string
      recogidaId: string | null
    }): Promise<void> => {
      const enCola = (await accionesPendientes()).find(
        (a) => a.clase === 'recogida' && a.ordenTrabajoId === prestamoId && !a.rechazada,
      )
      if (enCola) {
        await descartarAccion(enCola.id)
        return
      }
      if (!recogidaId) return

      const argumentos = { p_recogida_id: recogidaId }
      if (esFaltaDeSenal(null)) {
        await encolarAccion('cancelar-recogida', recogidaId, argumentos)
        return
      }
      const { error } = await supabase.rpc('fn_cancelar_recogida_herramienta', argumentos)
      if (error && esFaltaDeSenal(error)) {
        await encolarAccion('cancelar-recogida', recogidaId, argumentos)
        return
      }
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

/** Lo que me toca ir a recoger. Con respaldo: se mira en la calle. */
export function useMisRecogidas() {
  const consulta = useQuery({
    queryKey: ['mis-recogidas'],
    queryFn: () =>
      conRespaldo<MiRecogida[]>('mis-recogidas', async () => {
        const { data, error } = await supabase.rpc('fn_mis_recogidas')
        if (error) throw new Error(mensajeDeError(error))
        return data ?? []
      }),
  })

  return { recogidas: consulta.data ?? [], cargando: consulta.isLoading }
}

/** Gerencia, desde el tablero: qué operario va por ella (0239/0240). */
export function useAsignarRecogida() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async ({
      recogidaId,
      colaboradorId,
    }: {
      recogidaId: string
      colaboradorId: string
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_asignar_recogida_herramienta', {
        p_recogida_id: recogidaId,
        p_colaborador_id: colaboradorId,
      })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['lo-que-espera'] })
      await cliente.invalidateQueries({ queryKey: ['tablero'] })
    },
  })
}
